import { useEffect, useMemo, useState } from "react";
import { API_BASE } from "@/lib/api";
import { PageHeader, Card, Loader } from "@/components/ui/Primitives";
import {
  Users2, Wallet, TrendingUp, Sparkles, Building2, Banknote,
  CreditCard, Activity, RefreshCw, ShieldCheck, AlertCircle, Heart,
} from "lucide-react";

/**
 * Internal Business Workspace — Central Rentals Canada operators only.
 *
 * Token-gated: enters `?admin_token=…` via query string or via the inline
 * token gate. Token is stashed in localStorage as `crc_admin_token` so the
 * page survives a refresh.
 */
const STORAGE_KEY = "crc_admin_token";

export default function InternalWorkspace() {
  const initialTok = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("admin_token") || localStorage.getItem(STORAGE_KEY) || "";
    } catch (_) { return ""; }
  })();

  const [token, setToken] = useState(initialTok);
  const [draft, setDraft] = useState(initialTok);
  const [validated, setValidated] = useState(false);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchAll = async (tok) => {
    setLoading(true);
    setErr("");
    try {
      const [ovRes, tsRes] = await Promise.all([
        fetch(`${API_BASE}/internal/overview?token=${encodeURIComponent(tok)}`),
        fetch(`${API_BASE}/internal/timeseries?token=${encodeURIComponent(tok)}&days=30`),
      ]);
      if (ovRes.status === 401) {
        setErr("Invalid admin token");
        setValidated(false);
        setOverview(null);
        return;
      }
      if (!ovRes.ok) throw new Error("overview failed");
      const ov = await ovRes.json();
      const ts = tsRes.ok ? await tsRes.json() : null;
      setOverview(ov);
      setSeries(ts);
      setValidated(true);
      try { localStorage.setItem(STORAGE_KEY, tok); } catch (_) { /* noop */ }
    } catch (_) {
      setErr("Couldn't load — check the server and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    setToken(draft);
    fetchAll(draft);
  };

  if (!validated) {
    return (
      <div data-testid="internal-workspace-gate" className="max-w-md mx-auto py-16">
        <PageHeader
          title="Internal Workspace"
          subtitle="Central Rentals Canada operators only — please provide your admin token."
        />
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700">Admin token</label>
            <input
              data-testid="internal-token-input"
              type="password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder="ADMIN_CONFIG_TOKEN"
            />
            {err && <div data-testid="internal-token-error" className="text-xs text-rose-600">{err}</div>}
            <button
              data-testid="internal-token-submit"
              type="submit"
              disabled={!draft || loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? "Checking…" : "Unlock workspace"}
            </button>
          </form>
        </Card>
      </div>
    );
  }

  if (!overview) return <Loader />;

  return (
    <div data-testid="internal-workspace-page">
      <PageHeader
        title="Internal Workspace"
        subtitle={`Live data, no caching. Pulled from Mongo at ${new Date(overview.as_of).toLocaleTimeString("en-CA")}.`}
        actions={
          <button
            data-testid="internal-refresh"
            onClick={() => fetchAll(token)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        }
      />

      <RevenueRow rev={overview.revenue_cad} rent={overview.rent_processed} />

      <Section title="Audience" testid="section-audience" tone="sky">
        <Kpi testid="kpi-total-users" icon={Users2} label="Total users" value={overview.audience.total_users} sub={`${overview.audience.signups_today} today · ${overview.audience.signups_30d} this month`} />
        <Kpi testid="kpi-landlords" icon={Building2} label="Landlords" value={overview.audience.landlords} sub={`${overview.audience.tenants} tenants · ${overview.audience.investors} investors`} />
        <Kpi testid="kpi-signups-7d" icon={TrendingUp} label="Signups · 7d" value={overview.audience.signups_7d} sub={`${overview.audience.signups_today} today`} />
        <Kpi testid="kpi-active-7d" icon={Activity} label="Active · 7d" value={overview.audience.active_7d} sub="events logged" />
      </Section>

      <Section title="Activation" testid="section-activation" tone="emerald">
        <Kpi testid="kpi-activation-rate" icon={Sparkles} label="Activation rate" value={`${overview.activation.activation_rate_pct}%`} sub="landlords w/ ≥1 property" />
        <Kpi testid="kpi-properties" icon={Building2} label="Properties" value={overview.activation.total_properties} sub={`${overview.activation.active_leases} active leases`} />
        <Kpi testid="kpi-stripe-linked" icon={CreditCard} label="Stripe linked" value={overview.activation.stripe_linked} sub="connected payouts" />
        <Kpi testid="kpi-flinks-linked" icon={Banknote} label="Flinks linked" value={overview.activation.flinks_linked} sub="active bank links" />
      </Section>

      <Section title="Rent processed (30d)" testid="section-rent" tone="emerald">
        <Kpi testid="kpi-rent-30d" icon={Wallet} label="Rent collected" value={cad(overview.rent_processed.amount_30d_cents)} sub={`${overview.rent_processed.count_30d} payments`} />
        <Kpi testid="kpi-stp-rate" icon={Sparkles} label="STP rate" value={`${overview.rent_processed.stp_rate_pct}%`} sub={`${overview.rent_processed.cash_apps_auto_30d}/${overview.rent_processed.cash_apps_total_30d} auto-applied`} />
        <Kpi testid="kpi-pending-review" icon={AlertCircle} label="Pending review" value={overview.rent_processed.cash_apps_pending_review} sub="exceptions queue" tone={overview.rent_processed.cash_apps_pending_review > 0 ? "amber" : "slate"} />
        <Kpi testid="kpi-bank-txs" icon={Banknote} label="Bank txs 30d" value={overview.rent_processed.bank_transactions_30d} sub="from Flinks" />
      </Section>

      <Section title="Engagement" testid="section-engagement" tone="violet">
        <Kpi testid="kpi-smart-op-sessions" icon={Sparkles} label="Smart Op · 7d sessions" value={overview.engagement.smart_operator_sessions_7d} sub={`${overview.engagement.smart_operator_asks_7d} questions asked`} />
        <Kpi testid="kpi-referrals-30d" icon={Heart} label="Referrals · 30d" value={overview.engagement.referrals_30d} sub={`${overview.engagement.referrals_paid_total} ever paid out`} />
        <Kpi testid="kpi-community" icon={Users2} label="Community" value={overview.engagement.community_members} sub="profiles" />
        <Kpi testid="kpi-flinks-webhooks" icon={ShieldCheck} label="Flinks webhooks · 7d" value={overview.operational.flinks_webhooks_7d} sub="bank pings received" />
      </Section>

      {series && <TimeseriesPanel rows={series.rows} />}

      <div className="mt-6 text-xs text-slate-500 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3" />
        Central Rentals Canada operators only. This page reads live data from MongoDB on every request — no caching, no mocks.
      </div>
    </div>
  );
}

function RevenueRow({ rev, rent }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card className="p-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white relative overflow-hidden" data-testid="kpi-mrr">
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Monthly Recurring Revenue</div>
          <div className="font-display text-4xl font-extrabold mt-2 tabular-nums">{cad(rev.mrr_cents)}</div>
          <div className="text-xs opacity-90 mt-1">ARR ≈ {cad(rev.arr_cents)} · {rev.paying_landlords} paying landlords · ARPU {cad(rev.arpu_cents)}</div>
        </div>
      </Card>
      <Card className="p-6 bg-white" data-testid="kpi-rent-headline">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Rent processed · last 30d</div>
        <div className="font-display text-3xl font-extrabold text-slate-900 mt-2 tabular-nums">{cad(rent.amount_30d_cents)}</div>
        <div className="text-xs text-slate-500 mt-1">{rent.count_30d} payments · {rent.stp_rate_pct}% STP</div>
      </Card>
      <Card className="p-6 bg-white" data-testid="kpi-paying-headline">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Paying landlords</div>
        <div className="font-display text-3xl font-extrabold text-slate-900 mt-2 tabular-nums">{rev.paying_landlords}</div>
        <div className="text-xs text-slate-500 mt-1">ARPU {cad(rev.arpu_cents)}/mo</div>
      </Card>
    </div>
  );
}

function Section({ title, tone, children, testid }) {
  const dots = {
    emerald: "bg-emerald-500",
    sky: "bg-sky-500",
    violet: "bg-violet-500",
    amber: "bg-amber-500",
  };
  return (
    <section className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1.5 h-1.5 rounded-full ${dots[tone] || dots.emerald}`} />
        <h3 className="font-display text-sm font-bold text-slate-700 uppercase tracking-[0.16em]">{title}</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
    </section>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
          <div className="font-display text-2xl font-extrabold text-slate-900 mt-1 tabular-nums truncate">{value ?? "—"}</div>
          {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-none ${tones[tone] || tones.slate}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
}

function TimeseriesPanel({ rows }) {
  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.rent_cents || 0)), [rows]);
  const sumRent = rows.reduce((s, r) => s + (r.rent_cents || 0), 0);
  const sumSignups = rows.reduce((s, r) => s + (r.signups || 0), 0);
  const sumAsks = rows.reduce((s, r) => s + (r.smart_op_asks || 0), 0);
  return (
    <Card className="p-6" data-testid="section-timeseries">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-base font-bold text-slate-900">Daily volume — last {rows.length} days</h3>
          <p className="text-xs text-slate-500 mt-0.5">Signups · rent collected · Smart Operator questions</p>
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-4">
          <span><strong className="text-emerald-700">{cad(sumRent)}</strong> rent</span>
          <span><strong className="text-sky-700">{sumSignups}</strong> signups</span>
          <span><strong className="text-violet-700">{sumAsks}</strong> Smart Op asks</span>
        </div>
      </div>
      <div className="grid grid-cols-30 gap-px h-32 items-end" style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
        {rows.map((r) => {
          const h = Math.max(2, Math.round((r.rent_cents / max) * 100));
          return (
            <div key={r.date} title={`${r.date}\n${cad(r.rent_cents)} rent\n${r.signups} signups\n${r.smart_op_asks} Smart Op`} className="flex flex-col-reverse h-full">
              <div className="bg-emerald-500/80 hover:bg-emerald-600 rounded-t-sm transition-colors" style={{ height: `${h}%` }} />
              {r.signups > 0 && <div className="bg-sky-400/70 rounded-t-sm" style={{ height: `${Math.min(20, r.signups * 4)}px` }} />}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-slate-400 font-medium">
        <span>{rows[0]?.date}</span>
        <span>{rows[rows.length - 1]?.date}</span>
      </div>
    </Card>
  );
}

function cad(cents) {
  const v = (cents || 0) / 100;
  if (Math.abs(v) >= 1000) {
    return v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
  }
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}
